import { useEffect, useRef, useState } from "react";
import {
  FileSpreadsheet, Upload, RefreshCw, Sparkles, AlertTriangle, Save,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import { api, apiErrorMessage, formatRupiah, downloadFile } from "@/lib/api";

const BULAN = [
  "Januari", "Februari", "Maret", "April", "Mei", "Juni",
  "Juli", "Agustus", "September", "Oktober", "November", "Desember",
];

function formatRibuan(v) {
  const digits = String(v ?? "").replace(/\D/g, "");
  if (!digits) return "";
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

export default function AdminTidakTetap() {
  const now = new Date();
  const [bulan, setBulan] = useState(String(now.getMonth() + 1));
  const [tahun, setTahun] = useState(String(now.getFullYear()));
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [hasil, setHasil] = useState(null);
  const fileRef = useRef(null);

  const periode = `${tahun}-${String(bulan).padStart(2, "0")}`;

  async function load() {
    setLoading(true);
    try {
      const { data: d } = await api.get("/payroll/tidak-tetap", { params: { periode } });
      setData(d);
      const dr = {};
      (d.rows || []).forEach((r) => {
        Object.entries(r.nilai || {}).forEach(([kid, v]) => {
          dr[`${r.user_id}:${kid}`] = v ? String(Math.round(v)) : "";
        });
      });
      setDraft(dr);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [periode]);

  async function unduhTemplate() {
    setBusy(true);
    try {
      await downloadFile("/payroll/tidak-tetap/template", `TemplateTidakTetap_${periode}.xlsx`, { periode });
      toast.success("Template diunduh");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function unggah(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setHasil(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: res } = await api.post("/payroll/tidak-tetap/upload", fd, {
        params: { periode },
        headers: { "Content-Type": "multipart/form-data" },
      });
      setHasil(res);
      toast.success(`${res.disimpan} nilai komponen tersimpan untuk periode ${res.periode}`);
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function simpanSel(userId, komponenId) {
    const key = `${userId}:${komponenId}`;
    const nilai = parseInt((draft[key] || "0").replace(/\D/g, ""), 10) || 0;
    const awal = data?.rows?.find((r) => r.user_id === userId)?.nilai?.[komponenId] || 0;
    if (Math.round(awal) === nilai) return;
    try {
      await api.post("/payroll/tidak-tetap", {
        periode, user_id: userId, komponen_id: komponenId, nilai,
      });
      toast.success("Nilai disimpan");
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  }

  const komponen = data?.komponen || [];
  const rows = data?.rows || [];

  return (
    <div className="p-4 sm:p-8 max-w-[1400px] mx-auto" data-testid="page-tidak-tetap">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-teal-700">Administrasi</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 mt-1 leading-tight">
            Komponen Tidak Tetap
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Input nilai bulanan untuk komponen bersifat <b>Tidak Tetap</b> (insentif, bonus, uang proyek) —
            manual atau unggah Excel.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={load} disabled={loading} className="gap-2" data-testid="tt-refresh-btn">
            <RefreshCw size={14} /> Muat Ulang
          </Button>
          <Button variant="outline" onClick={unduhTemplate} disabled={busy} className="gap-2" data-testid="tt-template-btn">
            <FileSpreadsheet size={14} /> Unduh Template XLSX
          </Button>
          <Button
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            className="bg-teal-700 hover:bg-teal-800 gap-2"
            data-testid="tt-upload-btn"
          >
            <Upload size={14} /> {busy ? "Memproses…" : "Unggah Excel"}
          </Button>
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xlsm"
            onChange={unggah}
            className="hidden"
            data-testid="tt-file-input"
          />
        </div>
      </div>

      <div className="mt-6 rounded-xl border border-slate-200 bg-white p-5 grid grid-cols-1 sm:grid-cols-4 gap-4 items-end">
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Bulan Gaji</Label>
          <Select value={bulan} onValueChange={setBulan}>
            <SelectTrigger className="mt-1.5" data-testid="tt-bulan"><SelectValue /></SelectTrigger>
            <SelectContent>
              {BULAN.map((b, i) => (<SelectItem key={b} value={String(i + 1)}>{b}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Tahun</Label>
          <Select value={tahun} onValueChange={setTahun}>
            <SelectTrigger className="mt-1.5" data-testid="tt-tahun"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[0, 1, 2].map((d) => {
                const y = String(now.getFullYear() - d);
                return <SelectItem key={y} value={y}>{y}</SelectItem>;
              })}
            </SelectContent>
          </Select>
        </div>
        <div className="sm:col-span-2 rounded-lg border border-teal-100 bg-teal-50/60 p-3 text-xs text-teal-900">
          <div className="flex items-center gap-1.5 font-bold">
            <Sparkles size={13} /> Total nilai {data?.periode_label || periode}
          </div>
          <div className="text-lg font-extrabold mt-1" data-testid="tt-total">
            {formatRupiah(data?.total_nilai)}
          </div>
          {data?.periode_rentang ? (
            <div className="text-[11px] text-teal-800/80 mt-0.5">Cutoff {data.periode_rentang}</div>
          ) : null}
        </div>
      </div>

      {hasil ? (
        <div className="mt-4 rounded-lg border border-slate-200 bg-white p-4" data-testid="tt-hasil-upload">
          <div className="text-sm font-bold text-slate-800">
            Hasil unggah: {hasil.disimpan} nilai tersimpan
            {hasil.jumlah_dilewati ? `, ${hasil.jumlah_dilewati} baris dilewati` : ""}
          </div>
          {hasil.dilewati?.length ? (
            <ul className="mt-2 text-xs text-amber-800 space-y-1">
              {hasil.dilewati.map((d, i) => (
                <li key={i} className="flex items-start gap-1.5">
                  <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                  Baris {d.baris} ({d.npp}) — {d.alasan}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}

      {loading ? (
        <div className="mt-8 text-center text-slate-500 py-14">Memuat…</div>
      ) : komponen.length === 0 ? (
        <div className="mt-6 rounded-xl border border-slate-200 bg-white p-10 text-center" data-testid="tt-empty-komponen">
          <div className="mt-1 font-semibold text-slate-700">Belum ada komponen bersifat "Tidak Tetap"</div>
          <div className="text-xs text-slate-500 mt-1">
            Buat dulu di menu <b>Master Komponen</b> dengan sifat <b>Tidak Tetap</b>, lalu aktifkan pada tab Payroll pegawai.
          </div>
        </div>
      ) : rows.length === 0 ? (
        <div className="mt-6 rounded-xl border border-slate-200 bg-white p-10 text-center" data-testid="tt-empty-rows">
          <div className="mt-1 font-semibold text-slate-700">Belum ada pegawai yang mengaktifkan komponen Tidak Tetap</div>
          <div className="text-xs text-slate-500 mt-1">
            Aktifkan komponennya di <b>Master Pegawai → tab Payroll</b>.
          </div>
        </div>
      ) : (
        <div className="mt-4 rounded-xl border border-slate-200 bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead className="sticky left-0 bg-slate-50 z-20 min-w-[220px] shadow-[8px_0_10px_-6px_rgba(15,23,42,0.22)] border-r border-slate-200 uppercase text-[10px] tracking-widest font-bold text-slate-500">NPP &amp; Nama Pegawai</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Unit</TableHead>
                  {komponen.map((k) => (
                    <TableHead key={k.id} className="uppercase text-[10px] tracking-widest font-bold text-slate-500 whitespace-nowrap">
                      {k.nama}
                      {k.tipe === "Potongan" ? <span className="text-rose-600"> (pot.)</span> : null}
                    </TableHead>
                  ))}
                  <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500 text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.user_id} className="border-b border-slate-100" data-testid={`tt-row-${r.user_id}`}>
                    <TableCell className="sticky left-0 z-10 bg-white min-w-[220px] shadow-[8px_0_10px_-6px_rgba(15,23,42,0.22)] border-r border-slate-200">
                      <div className="font-semibold text-slate-900 whitespace-nowrap">{r.nama}</div>
                      <div className="text-[11px] text-slate-500 font-mono-clock">{r.npp}</div>
                    </TableCell>
                    <TableCell className="text-sm text-slate-600 whitespace-nowrap">{r.unit_nama}</TableCell>
                    {komponen.map((k) => {
                      const aktif = r.komponen_aktif.includes(k.id);
                      const key = `${r.user_id}:${k.id}`;
                      return (
                        <TableCell key={k.id}>
                          {aktif ? (
                            <div className="relative w-36">
                              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] font-semibold text-slate-400">Rp</span>
                              <Input
                                inputMode="numeric"
                                className="h-9 pl-7 text-sm font-mono-clock"
                                value={formatRibuan(draft[key] ?? "")}
                                onChange={(e) =>
                                  setDraft({ ...draft, [key]: e.target.value.replace(/\D/g, "").slice(0, 12) })
                                }
                                onBlur={() => simpanSel(r.user_id, k.id)}
                                placeholder="0"
                                data-testid={`tt-input-${r.user_id}-${k.id}`}
                              />
                            </div>
                          ) : (
                            <span className="text-xs text-slate-300">—</span>
                          )}
                        </TableCell>
                      );
                    })}
                    <TableCell className="text-right text-sm font-extrabold text-teal-800 whitespace-nowrap">
                      {formatRupiah(r.total)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="px-5 py-3 bg-slate-50 border-t border-slate-100 text-[11px] text-slate-500 flex items-center gap-1.5">
            <Save size={12} /> Nilai tersimpan otomatis saat Anda meninggalkan kolom. Nilai ini masuk ke kelompok
            <b> Pendapatan Tidak Tetap</b> pada slip gaji periode terpilih.
          </div>
        </div>
      )}
    </div>
  );
}
